(() => {
  const STATE_KEY = '__otheriverSiteMotion';
  const CONTROLLER_KEY = '__otheriverSiteMotionController';
  const MAX_LIBRARY_ATTEMPTS = 120;

  if (window[CONTROLLER_KEY]) {
    window[CONTROLLER_KEY].refresh();
    return;
  }

  function destroy() {
    const state = document.body?.[STATE_KEY];
    if (!state) return;

    state.cleanups.forEach((cleanup) => cleanup());
    state.context?.revert();
    state.progress?.remove();
    document.body.classList.remove('is-otheriver-motion-enhanced');
    delete document.body[STATE_KEY];
  }

  function init(attempt = 0) {
    if (!document.body || document.body[STATE_KEY]) return;
    if (!document.querySelector('.otheriver-section, .otheriver-site-header')) return;

    if (!window.gsap || !window.ScrollTrigger) {
      if (attempt < MAX_LIBRARY_ATTEMPTS) {
        window.setTimeout(() => init(attempt + 1), 50);
      }
      return;
    }

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const gsap = window.gsap;
    const ScrollTrigger = window.ScrollTrigger;
    const page = document.querySelector('#MainContent');
    const isDesktop = window.matchMedia('(min-width: 64rem)').matches;
    const hasServiceMotion = Boolean(page?.querySelector('[data-otheriver-service-motion]'));
    const cleanups = [];
    const progress = document.createElement('span');

    gsap.registerPlugin(ScrollTrigger);
    progress.className = 'otheriver-page-progress';
    progress.setAttribute('aria-hidden', 'true');
    document.body.append(progress);
    document.body.classList.add('is-otheriver-motion-enhanced');

    const context = gsap.context(() => {
      gsap.fromTo(
        progress,
        { scaleX: 0 },
        {
          scaleX: 1,
          ease: 'none',
          scrollTrigger: {
            start: 0,
            end: 'max',
            scrub: 0.25
          }
        }
      );

      const header = document.querySelector('.otheriver-site-header');
      if (header) {
        const headerTargets = [
          header.querySelector('.otheriver-site-header__logo'),
          header.querySelector('.otheriver-site-header__nav'),
          header.querySelector('.otheriver-site-header__contact'),
          header.querySelector('.otheriver-site-header__mobile')
        ].filter(Boolean);

        gsap.fromTo(
          headerTargets,
          { autoAlpha: 0, y: -18 },
          {
            autoAlpha: 1,
            y: 0,
            duration: 0.75,
            stagger: 0.08,
            ease: 'power3.out'
          }
        );
      }

      if (!page) return;

      const hero = page.querySelector('.otheriver-hero');
      if (hero && !hasServiceMotion) {
        const heroContent = hero.querySelector('.otheriver-hero__content');
        const heroParts = heroContent
          ? [
              heroContent.querySelector('.otheriver-eyebrow'),
              heroContent.querySelector('.otheriver-heading'),
              heroContent.querySelector('.otheriver-copy'),
              heroContent.querySelector('.otheriver-actions')
            ].filter(Boolean)
          : [];
        const heroVisual = hero.querySelector('.otheriver-hero__visual');

        gsap.fromTo(
          heroParts,
          {
            autoAlpha: 0,
            y: isDesktop ? 64 : 34,
            clipPath: 'inset(0 0 100% 0)',
            filter: 'blur(10px)'
          },
          {
            autoAlpha: 1,
            y: 0,
            clipPath: 'inset(0 0 0% 0)',
            filter: 'blur(0px)',
            duration: 1,
            stagger: 0.13,
            ease: 'power4.out'
          }
        );

        if (heroVisual) {
          gsap.fromTo(
            heroVisual,
            {
              autoAlpha: 0,
              x: isDesktop ? 70 : 0,
              y: isDesktop ? 0 : 38,
              rotateY: isDesktop ? -7 : 0,
              scale: 0.94
            },
            {
              autoAlpha: 1,
              x: 0,
              y: 0,
              rotateY: 0,
              scale: 1,
              duration: 1.2,
              delay: 0.28,
              ease: 'power4.out'
            }
          );

          gsap.to(heroVisual, {
            yPercent: isDesktop ? 10 : 5,
            ease: 'none',
            scrollTrigger: {
              trigger: hero,
              start: 'top top',
              end: 'bottom top',
              scrub: 0.8
            }
          });
        }

        const systemNodes = hero.querySelectorAll('.otheriver-system__node:not(.otheriver-system__node--shopify)');
        if (systemNodes.length) {
          gsap.fromTo(
            systemNodes,
            { autoAlpha: 0, scale: 0.72 },
            {
              autoAlpha: 1,
              scale: 1,
              duration: 0.65,
              stagger: 0.09,
              delay: 0.75,
              ease: 'back.out(1.7)'
            }
          );

          systemNodes.forEach((node, index) => {
            gsap.to(node, {
              y: index % 2 === 0 ? -5 : 5,
              duration: 2.4 + index * 0.25,
              ease: 'sine.inOut',
              repeat: -1,
              yoyo: true
            });
          });
        }
      }

      if (!hasServiceMotion) {
        page.querySelectorAll('.otheriver-section-heading').forEach((heading) => {
          const parts = Array.from(heading.children);
          gsap.fromTo(
            parts,
            {
              autoAlpha: 0,
              y: isDesktop ? 54 : 30,
              filter: 'blur(8px)'
            },
            {
              autoAlpha: 1,
              y: 0,
              filter: 'blur(0px)',
              duration: 0.9,
              stagger: 0.14,
              ease: 'power3.out',
              scrollTrigger: {
                trigger: heading,
                start: 'top 82%',
                toggleActions: 'play none none reverse'
              }
            }
          );
        });

        const revealItems = page.querySelectorAll(
          '.otheriver-card, .otheriver-home-path, .otheriver-feature__item, .otheriver-about-story__origin, .otheriver-about-story__profile, .otheriver-about-story__principles > *, .otheriver-contact__intro, .otheriver-contact__panel'
        );

        revealItems.forEach((item, index) => {
          gsap.fromTo(
            item,
            {
              autoAlpha: 0,
              x: isDesktop ? (index % 2 === 0 ? -34 : 34) : 0,
              y: isDesktop ? 46 : 34,
              scale: 0.97,
              filter: 'blur(7px)'
            },
            {
              autoAlpha: 1,
              x: 0,
              y: 0,
              scale: 1,
              filter: 'blur(0px)',
              duration: 0.85,
              ease: 'power3.out',
              scrollTrigger: {
                trigger: item,
                start: 'top 88%',
                toggleActions: 'play none none reverse'
              }
            }
          );
        });

        page.querySelectorAll('.otheriver-showcase__media img, .otheriver-feature__media img').forEach((image) => {
          gsap.fromTo(
            image,
            { scale: 1.07, yPercent: -3 },
            {
              scale: 1,
              yPercent: 3,
              ease: 'none',
              scrollTrigger: {
                trigger: image.closest('.otheriver-showcase__media, .otheriver-feature__media'),
                start: 'top bottom',
                end: 'bottom top',
                scrub: 0.75
              }
            }
          );
        });

        const manifesto = page.querySelector('.otheriver-manifesto');
        if (manifesto) {
          const manifestoParts = [
            manifesto.querySelector('.otheriver-eyebrow'),
            manifesto.querySelector('.otheriver-manifesto__heading'),
            manifesto.querySelector('.otheriver-copy'),
            manifesto.querySelector('.otheriver-actions')
          ].filter(Boolean);

          gsap.fromTo(
            manifestoParts,
            {
              autoAlpha: 0,
              y: isDesktop ? 68 : 38,
              scale: 0.97,
              filter: 'blur(9px)'
            },
            {
              autoAlpha: 1,
              y: 0,
              scale: 1,
              filter: 'blur(0px)',
              duration: 1,
              stagger: 0.13,
              ease: 'power4.out',
              scrollTrigger: {
                trigger: manifesto,
                start: 'top 80%',
                toggleActions: 'play none none reverse'
              }
            }
          );
        }
      }

      if (isDesktop) {
        document.querySelectorAll('.otheriver-button').forEach((button) => {
          const moveX = gsap.quickTo(button, 'x', { duration: 0.35, ease: 'power3.out' });
          const moveY = gsap.quickTo(button, 'y', { duration: 0.35, ease: 'power3.out' });

          const handleMove = (event) => {
            const bounds = button.getBoundingClientRect();
            moveX((event.clientX - bounds.left - bounds.width / 2) * 0.12);
            moveY((event.clientY - bounds.top - bounds.height / 2) * 0.18);
          };
          const handleLeave = () => {
            moveX(0);
            moveY(0);
          };

          button.addEventListener('pointermove', handleMove, { passive: true });
          button.addEventListener('pointerleave', handleLeave, { passive: true });
          cleanups.push(() => button.removeEventListener('pointermove', handleMove));
          cleanups.push(() => button.removeEventListener('pointerleave', handleLeave));
        });
      }
    }, document.body);

    const refresh = () => ScrollTrigger.refresh();
    window.addEventListener('load', refresh, { once: true });
    cleanups.push(() => window.removeEventListener('load', refresh));

    document.body[STATE_KEY] = { context, cleanups, progress };
    window.requestAnimationFrame(refresh);
  }

  function refresh() {
    destroy();
    window.requestAnimationFrame(() => init());
  }

  window[CONTROLLER_KEY] = { refresh };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', refresh);
  document.addEventListener('shopify:section:unload', refresh);
})();
